import { Controller, useFormContext, type FieldPath, type FieldValues, type RegisterOptions } from 'react-hook-form';
import MultiSelect, { type MultiSelectProps } from '@/components/common/MultiSelect';

/**
 * MultiSelectField
 * RHF-controlled wrapper around MultiSelect.
 * - Keeps form logic (value / error) outside the presentation component.
 * - Ensures value is always an array (MultiSelect expects string | number | null[]).
 */
export interface MultiSelectFieldProps<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends Omit<MultiSelectProps, 'name' | 'value' | 'onChange'> {
    name: TName;
    rules?: RegisterOptions<TFieldValues, TName>;
}

/**
 * Usage:
 * <MultiSelectField
 *   name="roles"
 *   control={control}
 *   options={[{ value: 'admin', label: 'Admin' }]}
 *   searchable
 * />
 */
const MultiSelectField = <
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
    name,
    rules,
    ...rest
}: MultiSelectFieldProps<TFieldValues, TName>) => {
    // Typed form context to align Control with TFieldValues
    const { control } = useFormContext<TFieldValues>();
    return (
        <Controller<TFieldValues, TName>
            name={name}
            control={control}
            rules={rules}
            render={({ field, fieldState }) => {
                const currentValue = Array.isArray(field.value) ? field.value : [];
                return (
                    <MultiSelect
                        {...rest}
                        name={name}
                        value={currentValue}
                        onChange={vals => field.onChange(vals)}
                        errorElement={fieldState.error?.message}
                        inputState={fieldState.error ? 'invalid' : 'normal'}
                    />
                );
            }}
        />
    );
};

export default MultiSelectField;
